import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { ApiError } from '@/api/errors';
import { Button } from '@/components/ui/button';

/** Tenant-scoped Boolean SystemConfig key gating pedigree creation on questionnaire submit. */
const CONFIG_KEY = 'questionnaire.pedigreeCreationEnabled';
const CONFIG_QUERY_KEY = ['system-config', CONFIG_KEY] as const;

/**
 * Admin control for the {@code questionnaire.pedigreeCreationEnabled} tenant config. BOOL configs
 * serialize as the canonical strings "true"/"false"; the catalog default is false, so a missing
 * override reads as unchecked.
 */
export function PedigreeCreationSettings(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(false);

  const currentQuery = useQuery<boolean>({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: async ({ signal }) => {
      try {
        const config = await apiClient.getConfig(CONFIG_KEY, { signal });
        return config.value === 'true';
      } catch (error) {
        // A missing key is the normal "not configured yet" state, not an error.
        if (error instanceof ApiError && error.status === 404) {
          return false;
        }
        throw error;
      }
    },
  });

  // Seed the checkbox once the stored value loads (and on refetch after save).
  useEffect(() => {
    if (currentQuery.isSuccess) {
      setChecked(currentQuery.data);
    }
  }, [currentQuery.isSuccess, currentQuery.data]);

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      await apiClient.upsertConfig(CONFIG_KEY, { value: next ? 'true' : 'false' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY }).catch(() => undefined);
      // Same SystemConfig store the Admin Configs table lists — keep it fresh.
      queryClient.invalidateQueries({ queryKey: ['admin', 'configs'] }).catch(() => undefined);
    },
  });

  return (
    <section aria-labelledby="admin-questionnaire-pedigree-heading" className="space-y-4">
      <div>
        <h2
          id="admin-questionnaire-pedigree-heading"
          className="text-base font-semibold text-foreground"
        >
          {t('routes.admin.questionnaires.pedigreeCreation.heading')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('routes.admin.questionnaires.pedigreeCreation.subheading')}
        </p>
      </div>

      <div className="flex max-w-2xl flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            checked={checked}
            disabled={currentQuery.isPending}
            onChange={(event) => {
              setChecked(event.target.checked);
            }}
          />
          {t('routes.admin.questionnaires.pedigreeCreation.label')}
        </label>
        <Button
          type="button"
          onClick={() => {
            save.mutate(checked);
          }}
          loading={save.isPending}
          disabled={currentQuery.isPending || checked === currentQuery.data}
        >
          {t('routes.admin.questionnaires.pedigreeCreation.save')}
        </Button>
      </div>

      {save.isError && (
        <p className="text-xs text-destructive">
          {t('routes.admin.questionnaires.pedigreeCreation.saveError')}
        </p>
      )}
    </section>
  );
}
